import React, { ComponentProps, forwardRef, Ref, useEffect, useRef, useState } from "react";

import classnames from "classnames";
import ReactPlayer from "react-player";

import { getContentProgress, updateUserProductProgress } from "~/api/productApi";
import { ActiveMediaPlayer } from "~/components/videoProductPage/components/ActiveMediaPlayer";
import { useUser } from "~/hooks/auth/useAuth";

import * as styles from "../../videoProductPage/common/VideoPlayer/VideoPlayer.css";

type ActiveMediaPlayerProps = ComponentProps<typeof ActiveMediaPlayer>;

interface Props {
  videoId: string;
  productId: string;
  shouldAutoPlay: boolean;
  playing: boolean;
  setPlaylistProgress(progress: number): void;
  setIsPlayingHandler(isPlaying: boolean): void;
}
export interface ProductProgressInfo {
  productId: string;
  contentId: string;
  percentageComplete: number;
  additionalSecondsWatched: number;
  positionInSeconds: number;
}

export const BTSVideoPlayer = forwardRef((props: Props & ActiveMediaPlayerProps) => {
  const {
    light: videoThumbnailOrShowDefault,
    width = "100%",
    height = "100%",
    playing,
    shouldAutoPlay,
    setIsPlayingHandler,
  } = props;

  const [lastVideoWatched, setLastVideoWatched] = useState<string>();
  const [lastSavedPosition, setLastSavedPosition] = useState(-1);
  const [lastProgressSentToBackend, setLastProgressSentToBackend] = useState(Date.now());
  const [didSaveToBackend, setDidSaveToBackend] = useState(false);

  const player = useRef<ReactPlayer>(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  useEffect(() => {
    const calculateStartTime = async () => {
      await getContentProgress(props.productId, props.videoId).then((progressData) => {
        let seekTo = 0;
        if (progressData.lastPositionInSeconds > 5) {
          seekTo = progressData.lastPositionInSeconds;
        }
        try {
          if (player.current) {

            player.current.seekTo(seekTo);
          }
        } catch (ex) {
          console.error("Error seeking to resume point");
        } finally {
          setIsPlayingHandler(shouldAutoPlay);
          setTimeout(() => {
            setShowOverlay(false);
          }, 200);
        }
      });
    };

    if (props.videoId !== lastVideoWatched) {
      setIsPlayingHandler(false);
      setShowOverlay(true);
      setLastVideoWatched(props.videoId);
      setDidSaveToBackend(false);
      setLastSavedPosition(-1);
    }

    if (isPlayerReady) {
      calculateStartTime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.videoId, isPlayerReady]);

  const saveVideoProgress = (info: { played: number; playedSeconds: number }) => {
    if (info.played <= 0) return;

    if (lastSavedPosition == -1) {
      setLastSavedPosition(info.playedSeconds);
    }

    props.setPlaylistProgress(info.played);
    if (
      Date.now() - lastProgressSentToBackend >= 5000 ||
      Math.abs(lastSavedPosition - info.playedSeconds) > 10 ||
      lastVideoWatched != props.videoId ||
      !didSaveToBackend
    ) {
      let additionalSecondsWatched = 0;
      if (
        playing &&
        info.playedSeconds > lastSavedPosition &&
        Math.abs(info.playedSeconds - lastSavedPosition) < 10
      ) {
        additionalSecondsWatched += info.playedSeconds - lastSavedPosition;
      }

      setLastSavedPosition(info.playedSeconds);
      updateUserProductProgress({
        productId: props.productId,
        contentId: props.videoId,
        percentageComplete: info.played,
        additionalSecondsWatched: additionalSecondsWatched,
        positionInSeconds: info.playedSeconds,
      });

      setLastProgressSentToBackend(Date.now());
      setDidSaveToBackend(true);
      return;
    } else {
      return;
    }
  };

  return (
    <>
      <div className={styles.container} style={{ height, width }}>
        <div
          className={styles.overlay}
          style={{
            opacity: showOverlay ? 0.97 : 0,
            visibility: showOverlay ? "visible" : "hidden",
          }}
        ></div>
        <div
          style={{ opacity: showOverlay ? 0 : 1, visibility: showOverlay ? "hidden" : "visible" }}
        >
          {props.videoId == lastVideoWatched && (
            <ActiveMediaPlayer
              {...props}
              ref={player}
              playing={playing}
              playIcon={<div></div>}
              controls={true}
              width={width}
              height={height}
              light={videoThumbnailOrShowDefault}
              progressInterval={100}
              onProgress={saveVideoProgress}
              className={classnames(styles.player, props.className)}
              onInterrupt={() => {
                setIsPlayingHandler(false);
              }}
              onReady={() => {
                setIsPlayerReady(true);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
});

BTSVideoPlayer.displayName = "BTSVideoPlayer";
